import React, { useEffect } from 'react';
import { WorkflowAssignment, TeamMember, Workflow } from '../../types';
import { useZodForm, useToast } from '../../hooks';
import { editAssignmentSchema, EditAssignmentFormData } from '../../schemas';
import Button from '../ui/Button';

interface EditAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (assignmentId: string, data: { workerId?: string; dueDate?: string }) => Promise<void>;
  assignment: WorkflowAssignment | null;
  teamMembers: TeamMember[];
  workflows: Workflow[];
}

const inputClasses = "w-full h-10 px-3 bg-neutral-100 rounded-md text-sm text-neutral-900 border border-neutral-200 focus:border-neutral-400 focus:outline-none";
const labelClasses = "block text-xs text-neutral-500 mb-1";

const EditAssignmentModal: React.FC<EditAssignmentModalProps> = ({
  isOpen,
  onClose,
  onSave,
  assignment,
  teamMembers,
}) => {
  const toast = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useZodForm<EditAssignmentFormData>(editAssignmentSchema, {
    defaultValues: { workerId: '', dueDate: '' },
    mode: 'onBlur',
  });

  useEffect(() => {
    if (assignment) {
      reset({
        workerId: assignment.workerId,
        dueDate: assignment.dueDate ? assignment.dueDate.split('T')[0] : '',
      });
    }
  }, [assignment, reset]);

  if (!isOpen || !assignment) return null;

  const onSubmit = async (data: EditAssignmentFormData) => {
    try {
      await onSave(assignment.id, {
        workerId: data.workerId !== assignment.workerId ? data.workerId : undefined,
        dueDate: data.dueDate || undefined,
      });
      onClose();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to update assignment');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-surface rounded-xl shadow-2xl border border-neutral-200 w-full max-w-xs mx-4">
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-sm font-semibold text-neutral-900">Edit Assignment</h2>
              <button
                type="button"
                onClick={onClose}
                className="text-neutral-400 hover:text-neutral-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div>
              <label className={labelClasses}>Assigned To</label>
              <select {...register('workerId')} className={inputClasses}>
                {teamMembers.map(m => (
                  <option key={m.id} value={m.id}>{m.fullName}</option>
                ))}
              </select>
              {errors.workerId && <p className="text-xs text-danger-500 mt-1">{errors.workerId.message}</p>}
            </div>

            <div>
              <label className={labelClasses}>Due Date</label>
              <input type="date" {...register('dueDate')} className={inputClasses} />
            </div>
          </div>

          <div className="px-5 pb-5 flex justify-end gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" size="sm" disabled={isSubmitting || !isDirty}>
              {isSubmitting ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAssignmentModal;
